/**
 * This component is responsible for displaying the standings of the division that the game is in.
 */
import React from "react";
import GameEvent from "./GameEvent";
import StandingsTable from "../core/StandingsTable";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Loading from "../core/Loading";

const processPreviewData = (jsonData, game_data) => {
  let data2 = game_data.data;

  // Declare the processed data object
  let processedData = {
    pre_game: {
      stats: [],
      title: data2.division.blip,
    },
    post_game: {
      stats: [],
      title: data2.division.blip,
    },
  };

  // Function to process and annotate the standings data
  try {
    jsonData.standings.forEach((record) => {
      processedData.pre_game.stats.push({
        footnotes: new Set(),
        logo: record.logo.lg,
        team_name: record.name,
        games: record.games,
        record: `${record.wins}-${record.losses}-${record.draws}`,
        points: record.points,
        gd: `${record.gf > record.ga ? "+" : ""}${record.gf - record.ga}`,
        gd_sort: record.gf - record.ga,
        w: record.wins,
        d: record.draws,
        l: record.losses,
        gf: record.gf,
        ga: record.ga,
        penalties: record.penalties,
        team_class:
          record.id === data2.home_team.id || record.id === data2.away_team.id
            ? "table_highlight"
            : "",
      });
    });

    jsonData.standings.forEach((record) => {
      let team_class = "";

      if (record.id === data2.home_team.id) {
        record.games += 1;
        if (data2.home_score > data2.away_score) {
          record.wins += 1;
          record.points += 2;
          record.gf += data2.home_score;
          record.ga += data2.away_score;
          team_class = "table_win";
        } else if (data2.home_score < data2.away_score) {
          record.losses += 1;
          record.gf += data2.home_score;
          record.ga += data2.away_score;
          team_class = "table_loss";
        } else {
          record.draws += 1;
          record.points += 1;
          record.gf += data2.home_score;
          record.ga += data2.away_score;
          team_class = "table_draw";
        }
      }

      if (record.id === data2.away_team.id) {
        record.games += 1;
        if (data2.home_score < data2.away_score) {
          record.wins += 1;
          record.points += 2;
          record.ga += data2.home_score;
          record.gf += data2.away_score;
          team_class = "table_win";
        } else if (data2.home_score > data2.away_score) {
          record.losses += 1;
          record.ga += data2.home_score;
          record.gf += data2.away_score;
          team_class = "table_loss";
        } else {
          record.draws += 1;
          record.points += 1;
          record.ga += data2.home_score;
          record.gf += data2.away_score;
          team_class = "table_draw";
        }
      }

      processedData.post_game.stats.push({
        footnotes: new Set(),
        logo: record.logo.lg,
        team_name: record.name,
        games: record.games,
        record: `${record.wins}-${record.losses}-${record.draws}`,
        points: record.points,
        gd: `${record.gf > record.ga ? "+" : ""}${record.gf - record.ga}`,
        gd_sort: record.gf - record.ga,
        w: record.wins,
        d: record.draws,
        l: record.losses,
        gf: record.gf,
        ga: record.ga,
        penalties: record.penalties,
        team_class: team_class,
      });
    });

    processedData.pre_game.stats.sort(function (a, b) {
      return (
        b.points - a.points ||
        b.gd_sort - a.gd_sort ||
        a.games - b.games ||
        b.w - a.w
      );
    });

    processedData.post_game.stats.sort(function (a, b) {
      return (
        b.points - a.points ||
        b.gd_sort - a.gd_sort ||
        a.games - b.games ||
        b.w - a.w
      );
    });
  } catch (e) {
    console.error(e);
  }

  return processedData;
};

const GamePreview = (game_data) => {
  const { gameId } = useParams();

  const { isLoading, error, data } = useQuery({
    staleTime: Infinity,
    queryKey: ["game", gameId, "standings"],
    queryFn: () =>
      fetch(`${process.env.REACT_APP_BUIHA_API_URL}/game/${gameId}/standings/`)
        .then((res) => res.json())
        .then((json) => {
          return processPreviewData(json, game_data);
        }),
  });

  if (isLoading)
    return (
      <Loading />
    );

  if (error) return "An error has occurred: " + error.message;

  let data2 = game_data.data;

  return (
    <div className="flex flex-col lg:flex-row w-full max-lg:items-center max-lg:justify-center">
      <div className="flex flex-col md:flex-col justify-between items-center w-full px-2">
        <GameEvent
          eventName={
            data2.status !== "UPCOMING" ? "Pre-Game Standings" : "Standings"
          }
          title={true}
        />
        <StandingsTable
          table_data={data.pre_game}
          extraClasses={"pt-2"}
          division_id={data2.division.id}
          expanded={data2.status === "UPCOMING"}
        />
      </div>
      {data2.status !== "UPCOMING" ? (
        <div className="flex flex-col md:flex-col justify-between items-center w-full px-2">
          <GameEvent eventName={"Post-Game Standings"} title={true} />
          <StandingsTable table_data={data.post_game} extraClasses={""} division_id={data2.division.id} />
        </div>
      ) : null}
    </div>
  );
};
export default GamePreview;
